import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const Peptides = () => {
  const navigate = useNavigate();
  const [currentBenefitIndex, setCurrentBenefitIndex] = useState(0);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        const offset = 90;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const pricingOptions = [
    {
      title: 'NAD+ Injection',
      price: 'Dose Dependent'
    },
    {
      title: 'Sermorelin Injection',
      price: 'Dose Dependent'
    },
    {
      title: 'BPC 157 Injection',
      price: 'Dose Dependent'
    },
    {
      title: 'GHK-CU Injection',
      price: 'Dose Dependent'
    },
    {
      title: 'MOTS-c Injection',
      price: 'Dose Dependent'
    },
    {
      title: 'B12 Injection',
      price: 'Dose Dependent'
    },
    {
      title: 'Lipo Stat+ Injection',
      price: 'Dose Dependent'
    }
  ];

  const benefits = [
    {
      title: 'Cellular Energy & Anti-Aging',
      description: 'NAD+ boosts cellular energy production, supports DNA repair, and promotes healthy aging at the cellular level.'
    },
    {
      title: 'Enhanced Recovery & Healing',
      description: 'BPC 157 accelerates tissue repair, reduces inflammation, and promotes healing of muscles, tendons, and ligaments.'
    },
    {
      title: 'Improved Sleep & Growth Hormone',
      description: 'Sermorelin stimulates natural growth hormone production, improving sleep quality, muscle mass, and metabolic function.'
    },
    {
      title: 'Skin Rejuvenation & Repair',
      description: 'GHK-CU promotes collagen synthesis, wound healing, and skin regeneration for a more youthful appearance.'
    },
    {
      title: 'Metabolic Optimization',
      description: 'MOTS-c enhances mitochondrial function, improves insulin sensitivity, and supports healthy metabolism.'
    },
    {
      title: 'Energy & Nerve Function',
      description: 'B12 injections boost energy levels, support nervous system health, and improve cognitive function.'
    },
    {
      title: 'Weight Loss Support',
      description: 'Lipo Stat+ aids in fat metabolism, liver health, and weight management when combined with proper diet and exercise.'
    },
    {
      title: 'Reduces Inflammation',
      description: 'Multiple peptides work to decrease systemic inflammation, promoting overall wellness and recovery.'
    },
    {
      title: 'Supports Immune Function',
      description: 'Peptide therapy strengthens immune response and enhances the body\'s natural defense mechanisms.'
    },
    {
      title: 'Enhances Physical Performance',
      description: 'Improves endurance, strength, and athletic performance through optimized cellular function.'
    },
    {
      title: 'Cognitive Enhancement',
      description: 'Supports brain health, mental clarity, focus, and overall cognitive performance.'
    },
    {
      title: 'Personalized Treatment',
      description: 'Customized peptide protocols tailored to your specific health goals and wellness needs.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Peptide Therapy - NAD+, Sermorelin, BPC 157 | Century Aesthetics</title>
        <meta name="description" content="Advanced peptide injections including NAD+, Sermorelin, and BPC 157 for anti-aging, healing, and cellular energy in Forest Hills, NY." />
        <meta name="keywords" content="peptide therapy, NAD+ injections, sermorelin, BPC 157, anti-aging peptides, peptide treatments" />
        <link rel="canonical" href="https://www.centuryaesthetics.org/peptides" />
        
        <meta property="og:title" content="Peptide Therapy | Century Longevity & Aesthetics" />
        <meta property="og:description" content="Advanced peptide injections including NAD+, Sermorelin, and BPC 157 for anti-aging, healing, and cellular energy in Forest Hills, NY." />
        <meta property="og:url" content="https://www.centuryaesthetics.org/peptides" />
      </Helmet>

      <div className="min-h-screen bg-white pt-28">

      {/* Main Content Section */}
      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-lg overflow-hidden shadow-2xl h-[500px]">
                <img 
                  src="/pep.webp" 
                  alt="Peptide Therapy Injections" 
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-6">
                PEPTIDE THERAPY: ADVANCED INJECTIONS FOR OPTIMAL HEALTH & PERFORMANCE
              </h2>
              <div className="w-20 h-1 bg-gold mb-6"></div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Peptide therapy uses specialized amino acid sequences to optimize cellular function, enhance recovery, and promote overall wellness. Our comprehensive peptide menu includes NAD+ for cellular energy and anti-aging, BPC 157 for tissue repair and healing, Sermorelin for growth hormone optimization, GHK-CU for skin rejuvenation, MOTS-c for metabolic enhancement, B12 for energy and nerve function, and Lipo Stat+ for weight management support. Each peptide targets specific biological pathways to deliver targeted therapeutic benefits.
                </p>
                <p>
                  Administered through convenient injections, peptide therapy offers superior bioavailability compared to oral supplements. Treatment protocols are personalized based on your health goals, whether you're seeking improved recovery, enhanced performance, anti-aging benefits, metabolic optimization, or overall wellness support. Dosing is customized to your individual needs under medical supervision. Schedule your consultation at Century Longevity & Aesthetics to discover how peptide therapy can optimize your health and performance.
                </p>
              </div>

              <motion.button
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="mt-8 bg-olive-darkest text-white px-8 py-4 rounded font-medium text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Now
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 bg-olive-darkest">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-white mb-4">Peptide Therapy Pricing</h2>
            <div className="w-20 h-1 bg-gold mx-auto mb-6"></div>
            <div className="inline-flex items-center gap-3 bg-gold/10 border-2 border-gold px-8 py-4 rounded-lg">
              <CheckCircle className="w-6 h-6 text-gold" />
              <p className="text-white text-lg font-medium">
                Free Consultation for Peptide Therapy Services
              </p>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {pricingOptions.map((option, index) => (
              <motion.div
                key={option.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="bg-white rounded-lg p-8 shadow-xl text-center"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gold/10 flex items-center justify-center">
                  <svg 
                    className="w-10 h-10 text-gold" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={1.5} 
                      d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" 
                    />
                  </svg>
                </div>
                
                <h3 className="text-lg font-heading font-semibold text-olive-darkest mb-3 min-h-[3rem] flex items-center justify-center">
                  {option.title}
                </h3>
                <p className="text-3xl font-heading font-light text-gold mb-6">
                  {option.price}
                </p>
                
                <motion.button
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={scrollToContact}
                  className="w-full bg-olive-darkest text-white px-6 py-3 rounded-lg font-medium text-sm tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg"
                >
                  Book Now
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-4">Benefits of Peptide Therapy</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {/* Carousel Container */}
            <div className="relative">
              {/* Previous Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === 0 ? benefits.length - 1 : prev - 1))}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Previous benefit"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              {/* Next Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === benefits.length - 1 ? 0 : prev + 1))}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Next benefit"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Carousel Card */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentBenefitIndex}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-lg shadow-xl p-8 sm:p-12 min-h-[300px] flex flex-col justify-center"
                >
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <CheckCircle className="w-16 h-16 text-gold" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-3xl font-semibold text-olive-darkest mb-4">
                        {benefits[currentBenefitIndex].title}
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        {benefits[currentBenefitIndex].description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Dots Indicator */}
              <div className="flex justify-center gap-2 mt-8">
                {benefits.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentBenefitIndex(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === currentBenefitIndex 
                        ? 'w-8 bg-gold' 
                        : 'w-2 bg-gray-300 hover:bg-gold/50'
                    }`}
                    aria-label={`Go to benefit ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-16 bg-olive-darkest text-white p-8 sm:p-10 md:p-12 rounded-xl shadow-2xl text-center"
          >
            <p className="text-base sm:text-lg leading-relaxed mb-8 text-white">
              Optimize your health, performance, and longevity with advanced peptide therapy tailored to your unique needs!
            </p>
            <motion.button
              whileHover={{ y: -2, scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={scrollToContact}
              className="w-full sm:w-auto bg-gold text-olive-darkest px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation mb-10"
            >
              Schedule Consultation
            </motion.button>

            <div className="w-24 h-0.5 bg-gold/30 mx-auto mb-8"></div>

            <h3 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold mb-4 text-white">Have Questions?</h3>
            <p className="text-base sm:text-lg text-white mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
              Learn about peptide therapy, which peptides are right for you, and how they can enhance your health and wellness.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  navigate('/faq');
                  setTimeout(() => {
                    const element = document.getElementById('peptides');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-white px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                View FAQs
              </motion.button>
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-gold px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                Contact Us Directly
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Peptides;